import { DropboxFileItem } from '@common/types/dropbox';
import { DropboxFilesListTable } from '@components/display/tables';
import { useMediaAssetsContext } from '@mediaAssets/contexts';
import {
    useLazyGetMediaAssetsDropboxFilesLoadedPathsQuery,
    useMediaAssetsFavoriteMutation,
    useMediaAssetsUnfavoriteMutation,
} from '@mediaAssets/services';
import { useCallback, useMemo } from 'react';
import { defineMessage } from 'react-intl';

export const MediaAssetsListView = () => {
    const { visibleMediaAssets, setVisibleMediaAssets, setGalleryOpen, setSelectedMediaAsset } =
        useMediaAssetsContext();

    const [triggerLoadedPaths] = useLazyGetMediaAssetsDropboxFilesLoadedPathsQuery();

    const [favoriteFile] = useMediaAssetsFavoriteMutation();
    const [unfavoriteFile] = useMediaAssetsUnfavoriteMutation();

    const loadPage = useCallback(
        async ({
            queryPath,
            sortParam,
            pageSize,
            loadedPaths,
        }: {
            queryPath: string;
            sortParam: string | null;
            pageSize: number;
            loadedPaths: string[];
        }) => {
            const pageData = await triggerLoadedPaths(
                {
                    path: queryPath,
                    page_size: pageSize,
                    ordering: sortParam ?? undefined,
                    loaded_paths: loadedPaths,
                },
                true
            ).unwrap();

            return {
                results: pageData.results ?? [],
                count: pageData.count,
            };
        },
        [triggerLoadedPaths]
    );

    const handleFavorite = useCallback(
        async ({ path, folderPath }: { path: string; folderPath: string }) => {
            await favoriteFile({ path, folderPath }).unwrap();
        },
        [favoriteFile]
    );

    const handleUnfavorite = useCallback(
        async ({ path, folderPath }: { path: string; folderPath: string }) => {
            await unfavoriteFile({ path, folderPath }).unwrap();
        },
        [unfavoriteFile]
    );

    const handlePreviewRowClick = useCallback(
        (item: DropboxFileItem) => {
            if (!setGalleryOpen || !setSelectedMediaAsset) {
                return;
            }
            setSelectedMediaAsset(item);
            setGalleryOpen(true);
        },
        [setGalleryOpen, setSelectedMediaAsset]
    );

    const favoriteStates = useMemo(
        () =>
            Object.fromEntries(
                visibleMediaAssets
                    .filter((asset) => Boolean(asset.path))
                    .map((asset) => [asset.path as string, Boolean(asset.is_favorite)])
            ),
        [visibleMediaAssets]
    );

    const handleFavoriteStateChange = useCallback(
        ({ path, isFavorite }: { path: string; isFavorite: boolean }) => {
            setVisibleMediaAssets((prev) =>
                prev.map((asset) => (asset.path === path ? { ...asset, is_favorite: isFavorite } : asset))
            );
            setSelectedMediaAsset((prev) => (prev && prev.path === path ? { ...prev, is_favorite: isFavorite } : prev));
        },
        [setSelectedMediaAsset, setVisibleMediaAssets]
    );

    return (
        <DropboxFilesListTable
            routeSegment="media-assets"
            downloadType="media_assets"
            emptyListText={defineMessage({
                id: 'mediaAssets.emptyList',
                defaultMessage: 'No media assets found in this folder.',
            })}
            loadErrorMessage={defineMessage({
                id: 'mediaAssets.loadError',
                defaultMessage: 'Error loading media assets',
            })}
            loadPage={loadPage}
            onFavorite={handleFavorite}
            onUnfavorite={handleUnfavorite}
            externalFavoriteStates={favoriteStates}
            onFavoriteStateChange={handleFavoriteStateChange}
            onVisibleMediaItemsChange={setVisibleMediaAssets}
            onPreviewRowClick={handlePreviewRowClick}
        />
    );
};
